import { faChevronDown, faChevronUp, faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import { formatBillingFrequency } from 'utils/MembershipUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { FunctionComponent, MouseEvent, useMemo, useState } from 'react';
import { Membership } from '@spike/membership-model';
import { useCommonStyles } from './CommonStyles';
import { faBan, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import { ToolbarButton } from 'components/UI/Toolbar/ToolbarButton';
import { ConfirmDialog, ToolbarItem } from 'components/UI';
import Button from 'components/UI/V2/Button/Button';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import EditMembershipNameModal from 'components/Memberships/Modals/EditMembershipNameModal';

interface MembershipRowProps {
    membership: Membership;
    expanded: boolean;
    onToggle: () => void;
    onEdit?: () => void;
    onEditName?: (membershipId: number, newName: string) => void;
    editingMembershipId?: number | null;
    className?: string;
    onChangeStatus: (membershipId: number, isActive: boolean) => void;
    onSelectMembership: (membership: Membership) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        membershipRow: {
            cursor: 'pointer',
            [theme.breakpoints.down('sm')]: {
                alignItems: 'center',
                justifyContent: 'space-between'
            },
            [theme.breakpoints.up('md')]: {
                padding: '0px',
                minHeight: '73px'
            }
        },
        cellText: {
            fontFamily: 'Poppins',
            fontWeight: 400,
            color: 'black',
            fontSize: '15px'
        },
        expandIcon: {
            cursor: 'pointer',
            fontSize: 18,
            [theme.breakpoints.down('md')]: {
                display: 'none'
            }
        },
        desktop: {
            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        headerActionPopup: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '25px'
        },
        headerActionPopupTitle: {
            fontWeight: 600,
            color: '#000000',
            fontSize: '16px',
            textAlign: 'left',
            display: 'block',
            width: '100%'
        },
        confirmQuestion: {
            paddingLeft: '15px',
            paddingRight: '15px',
            [theme.breakpoints.up('sm')]: {
                maxWidth: '500px',
                margin: '0 auto'
            }
        },
        membershipOptionsButton: {
            'all': 'unset',
            'fontSize': 21,
            '& svg': {
                display: 'block'
            }
        },
        toolbarButton: {
            fontSize: 18,
            marginRight: 14
        }
    })
);

const MembershipRow: FunctionComponent<MembershipRowProps> = props => {
    const { membership, expanded, onToggle, onEdit, onEditName, editingMembershipId, className, onChangeStatus } =
        props;
    const classes = useStyles();
    const commonClasses = useCommonStyles();
    const [showMobileDropdown, setShowMobileDropdown] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [actionType, setActionType] = useState<'activate' | 'deactivate'>('deactivate');
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    useHideHubSpotChat(showMobileDropdown || showEditModal);

    const handleMembershipClick = (event: MouseEvent) => {
        event.stopPropagation();
        props.onSelectMembership(props.membership);
    };

    const handleMoreInfoClick = (event: MouseEvent, type: 'activate' | 'deactivate') => {
        event.stopPropagation();
        setActionType(type);
        setShowConfirmDialog(true);
    };

    const handleEditClick = (event: MouseEvent) => {
        event.stopPropagation();
        setShowEditModal(true);
    };

    const handleSaveName = (newName: string) => {
        if (onEditName && membership.id) {
            onEditName(membership.id, newName);
        }
    };

    /*
    const totalAmount = useMemo(
        () => membership.membershipItems.reduce((total, item) => total + item.amount * item.quantity, 0),
        [membership.membershipItems]
    );
    */

    const totalAmount = useMemo(() => Number(membership.total)?.toFixed(2) ?? '0.00', [membership.total]);

    const headerActionPopup = (
        <Box className={classes.headerActionPopup}>
            <Typography className={classes.headerActionPopupTitle}>{membership.name}</Typography>
        </Box>
    );

    return (
        <Grid
            container
            item
            xs={12}
            className={clsx(classes.membershipRow, className)}
        >
            <Grid
                item
                xl={3}
                lg={4}
                xs={10}
                className={clsx(commonClasses.cellVerticalCenter)}
                onClick={handleMembershipClick}
            >
                <Typography
                    className={clsx(classes.cellText, commonClasses.cellCenter, commonClasses.extraInfoWrapper)}
                >
                    {membership.name.length > 45 ? `${membership.name.substring(0, 45)}...` : membership.name}
                    <Typography className={clsx(commonClasses.capitalize, commonClasses.extraInfo)}>
                        ${totalAmount}
                    </Typography>
                </Typography>
            </Grid>
            <Grid
                item
                xs={1}
                lg={2}
                className={clsx(
                    commonClasses.typeCell,
                    commonClasses.cellVerticalCenter,
                    commonClasses.cellLeft,
                    classes.desktop
                )}
                onClick={handleMembershipClick}
            >
                <Typography className={clsx(classes.cellText, commonClasses.capitalize)}>
                    {membership.enrollmentType}
                </Typography>
            </Grid>
            <Grid
                item
                xs={1}
                className={clsx(
                    commonClasses.smallWidthCell,
                    commonClasses.cellVerticalCenter,
                    commonClasses.cellLeft,
                    classes.desktop
                )}
                onClick={handleMembershipClick}
            >
                <Typography
                    style={{ minWidth: '50px', textAlign: 'center' }}
                    className={classes.cellText}
                >
                    {membership.enrollmentType == 'subscription' ? formatBillingFrequency(membership) || '-' : '-'}
                </Typography>
            </Grid>
            <Grid
                item
                xs={1}
                className={clsx(
                    commonClasses.smallWidthCell,
                    commonClasses.cellVerticalCenter,
                    commonClasses.cellRight,
                    classes.desktop
                )}
                onClick={handleMembershipClick}
            >
                <Typography className={clsx(classes.cellText, classes.desktop, commonClasses.cellCenter)}>
                    {membership.subscribedCount ?? 0}
                </Typography>
            </Grid>
            <Grid
                item
                xs={1}
                className={clsx(
                    commonClasses.smallWidthCell,
                    commonClasses.cellVerticalCenter,
                    commonClasses.cellRight,
                    classes.desktop
                )}
                onClick={handleMembershipClick}
            >
                <Typography className={clsx(classes.cellText, classes.desktop, commonClasses.cellCenter)}>
                    {membership.canceledCount ?? 0}
                </Typography>
            </Grid>
            <Grid
                item
                xs={1}
                className={clsx(
                    commonClasses.smallWidthCell,
                    commonClasses.cellVerticalCenter,
                    commonClasses.cellRight,
                    classes.desktop
                )}
                onClick={handleMembershipClick}
            >
                <Typography className={clsx(classes.cellText, classes.desktop, commonClasses.cellCenter)}>
                    {membership.pausedCount ?? 0}
                </Typography>
            </Grid>
            <Grid
                item
                xs={1}
                className={clsx(
                    commonClasses.smallWidthCell,
                    commonClasses.cellVerticalCenter,
                    commonClasses.cellRight,
                    classes.desktop
                )}
                onClick={handleMembershipClick}
            >
                <Typography className={clsx(classes.cellText, classes.desktop, commonClasses.cellCenter)}>
                    ${totalAmount}
                </Typography>
            </Grid>
            <Grid
                item
                xs={1}
                style={{ paddingRight: '20px' }}
                className={clsx(
                    commonClasses.smallWidthCell,
                    commonClasses.cellVerticalCenter,
                    commonClasses.cellRight,
                    commonClasses.actions
                )}
            >
                {showMobileDropdown && isMobile && (
                    <ActionsPopup
                        header={headerActionPopup}
                        actions={[
                            {
                                label: 'Edit Membership',
                                icon: faPencil,
                                onClick: () => {
                                    onEdit && onEdit();
                                    setShowMobileDropdown(false);
                                }
                            },
                            /*
                            {
                                label: 'Edit Name',
                                icon: faPencil,
                                onClick: () => {
                                    setShowEditModal(true);
                                    setShowMobileDropdown(false);
                                }
                            },
                            */
                            {
                                label: membership.active ? 'Deactivate' : 'Activate',
                                icon: membership.active ? faBan : faCircleCheck,
                                onClick: () => {
                                    setActionType(membership.active ? 'deactivate' : 'activate');
                                    setShowConfirmDialog(true);
                                    setShowMobileDropdown(false);
                                }
                            }
                        ]}
                        onClose={() => setShowMobileDropdown(false)}
                    />
                )}

                {isMobile && (
                    <button
                        type="button"
                        aria-label="Membership actions"
                        className={classes.membershipOptionsButton}
                        onClick={e => {
                            e.stopPropagation();
                            setShowMobileDropdown(true);
                        }}
                    >
                        <FontAwesomeIcon icon={faEllipsis} />
                    </button>
                )}

                {showConfirmDialog && (
                    <ConfirmDialog
                        open={showConfirmDialog}
                        title={actionType === 'deactivate' ? 'Deactivate Membership' : 'Activate Membership'}
                        question={
                            <Typography className={classes.confirmQuestion}>
                                {actionType === 'deactivate'
                                    ? 'Are you sure you want to deactivate this membership? Any memberships previously sold will remain active.'
                                    : 'Are you sure you want to activate this membership?'}
                            </Typography>
                        }
                        onCancel={() => setShowConfirmDialog(false)}
                        actions={
                            <>
                                <Button
                                    fullWidth
                                    size="large"
                                    variant="black"
                                    label="Cancel"
                                    style={{ minWidth: 'unset' }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        setShowConfirmDialog(false);
                                    }}
                                />
                                <Button
                                    fullWidth
                                    size="large"
                                    variant={actionType === 'deactivate' ? 'danger' : 'green'}
                                    label={actionType === 'deactivate' ? 'Yes, Deactivate' : 'Yes, Activate'}
                                    style={{ margin: 0, minWidth: 'unset' }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        setShowConfirmDialog(false);
                                        onChangeStatus && onChangeStatus(membership.id!, actionType === 'activate');
                                    }}
                                />
                            </>
                        }
                    />
                )}

                {showEditModal && (
                    <EditMembershipNameModal
                        currentName={membership.name}
                        onSave={handleSaveName}
                        onCancel={() => setShowEditModal(false)}
                        isLoading={editingMembershipId === membership.id}
                    />
                )}

                {!isMobile && (
                    <ToolbarButton
                        id="memberships_toolbar"
                        className={classes.toolbarButton}
                    >
                        <ToolbarItem
                            text="Edit Membership"
                            icon={faPencil}
                            onClick={(event: MouseEvent) => {
                                event.stopPropagation();
                                onEdit && onEdit();
                            }}
                        />
                        {/*
                        <ToolbarItem
                            text="Edit Name"
                            icon={faPencil}
                            onClick={handleEditClick}
                        />
                        */}
                        <ToolbarItem
                            text={membership.active ? 'Deactivate' : 'Activate'}
                            icon={membership.active ? faBan : faCircleCheck}
                            onClick={(event: MouseEvent) => {
                                handleMoreInfoClick(event, membership.active ? 'deactivate' : 'activate');
                            }}
                        />
                    </ToolbarButton>
                )}

                {/*
                <FontAwesomeIcon
                    icon={expanded ? faChevronUp : faChevronDown}
                    className={classes.expandIcon}
                    onClick={(event: MouseEvent) => {
                        event.stopPropagation();
                        onToggle();
                    }}
                />
                */}
            </Grid>
        </Grid>
    );
};

export default MembershipRow;
